import { RefObject, useEffect, useRef } from 'react';

type AutoScrollVisibility = 'partial' | 'full';

interface UseAutoScrollIntoViewOptions {
    viewportRef: RefObject<HTMLElement | null>;
    getTarget: () => HTMLElement | null;
    enabled?: boolean;
    visibility?: AutoScrollVisibility;
    scrollKey?: unknown;
    behavior?: ScrollBehavior;
}

function getCenteredScrollTop(viewport: HTMLElement, target: HTMLElement) {
    const viewportRect = viewport.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    return (
        viewport.scrollTop +
        targetRect.top -
        viewportRect.top -
        (viewport.clientHeight - targetRect.height) / 2
    );
}

function clampScroll(value: number, max: number) {
    return Math.min(Math.max(value, 0), Math.max(max, 0));
}

export function useAutoScrollIntoView({
    viewportRef,
    getTarget,
    enabled = true,
    visibility = 'partial',
    scrollKey,
    behavior = 'auto',
}: UseAutoScrollIntoViewOptions) {
    const getTargetRef = useRef(getTarget);

    useEffect(() => {
        getTargetRef.current = getTarget;
    }, [getTarget]);

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!enabled) return;

        const viewport = viewportRef.current;
        const target = getTargetRef.current();

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!viewport || !target) return;

        const viewportRect = viewport.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const isVisible =
            visibility === 'full'
                ? targetRect.top >= viewportRect.top &&
                  targetRect.bottom <= viewportRect.bottom
                : targetRect.bottom >= viewportRect.top &&
                  targetRect.top <= viewportRect.bottom;

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (isVisible) return;

        requestAnimationFrame(() => {
            viewport.scrollTo({
                top: clampScroll(
                    getCenteredScrollTop(viewport, target),
                    viewport.scrollHeight - viewport.clientHeight,
                ),
                behavior,
            });
        });
    }, [scrollKey, enabled, getTargetRef, behavior, viewportRef, visibility]);
}
